/**
 * reCAPTCHA v3 + v2 Fallback Service
 *
 * Primary: Uses invisible reCAPTCHA v3 (no user interaction)
 * Fallback: When v3 score fails, allows v2 challenge modal
 */
import { get } from 'lodash';
import api from '@/services/api';
import { loadScript } from '@/utils/helpers';

let v3SiteKey = null;
let v2SiteKey = null;
let grecaptchaInstance = null;

export default {
  /**
   * Initialize the v3 reCAPTCHA script
   * @returns {Promise<object>} The grecaptcha instance
   */
  async initV3() {
    if (grecaptchaInstance && v3SiteKey) {
      return grecaptchaInstance;
    }

    v3SiteKey = await api.account.getCaptchaSiteKeyV3().then(r => r.data);
    const scriptUrl = `https://www.google.com/recaptcha/api.js?render=${v3SiteKey}`;
    grecaptchaInstance = await loadScript(scriptUrl, 'grecaptcha');
    return grecaptchaInstance;
  },

  /**
   * Execute invisible v3 reCAPTCHA and get token
   * @param {string} action - The action name for scoring (e.g., 'login', 'register')
   * @returns {Promise<string>} The reCAPTCHA token
   */
  async executeV3(action) {
    const grecaptcha = await this.initV3();
    return new Promise((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(v3SiteKey, { action }).then(resolve);
      });
    });
  },

  /**
   * Get the v2 site key for fallback challenge modal
   * @returns {Promise<string>} The v2 site key
   */
  async getV2SiteKey() {
    if (!v2SiteKey) {
      v2SiteKey = await api.account.getCaptchaSiteKey().then(r => r.data);
    }
    return v2SiteKey;
  },

  /**
   * Check if an error response indicates v3 score failed and v2 challenge is needed
   * @param {object} error - Axios error object
   * @returns {boolean} True if v2 challenge is required
   */
  isV3ScoreFailure(error) {
    // Check for OAuth token endpoint error format
    const oauthError = get(error, 'response.data.error');
    if (oauthError === 'recaptcha_challenge_required') {
      return true;
    }
    // Check for standard API error format
    const data = get(error, 'response.data', []);
    if (Array.isArray(data)) {
      return data.some(e => e.code === 'recaptcha_challenge_required');
    }
    return false;
  },
};



// WEBPACK FOOTER //
// ./src/services/recaptcha.js